<script setup lang="ts">
  import { ref, onMounted, watch, onUnmounted } from 'vue'
  import {
    FlowInsuredTravelersWidget,
    FlowTravelerFormWidget,
    FlowPersonalInformationWidget,
  } from '@/widgets/Flow'
  import { useCommonStore, useTravelersStore, useUserStore } from '@/shared/store'
  import type { TravelerInformation as ApiTravelerInformation } from '@/shared/types/api.types'
  import { HOLDER_ID } from '@/widgets/Flow/FlowInsuredTravelersWidget/FlowInsuredTravelersWidget.constants'
  import {
    useApiRequestsAbort,
    useWinterSport,
    useGoogleAnalytics,
    usePerformanceTiming,
  } from '@/shared/composables'
  import { TravelDirection } from '@/shared/types/common.types'
  import { useFormDataWithRedirect } from '@/shared/composables/useFormDataWithRedirect'

  interface Emits {
    (e: 'proceed'): void
    (e: 'load', request: () => Promise<any>): void
    (e: 'back'): void
  }
  const emit = defineEmits<Emits>()

  useApiRequestsAbort()

  const { getFormData } = useFormDataWithRedirect()
  const travelersStore = useTravelersStore()
  const commonStore = useCommonStore()
  const userStore = useUserStore()
  const { withWinterSport } = useWinterSport()
  // const { withWinterSport, fetchWinterSportPrice: _fetchWinterSportPrice } = useWinterSport();
  const gaService = useGoogleAnalytics()
  const { onPerformanceCheckEnd } = usePerformanceTiming()

  const isTravelerFormActive = ref(false)
  const isHolderFormActive = ref(false)
  const travelerInformation = ref<ApiTravelerInformation | null>(null)
  const travelersInformation = ref<ApiTravelerInformation[]>([])
  const winterSportPrice = ref(0)

  const onProceed = () => {
    emit('proceed')
  }

  const fetchTravellers = async () => {
    travelersInformation.value = await travelersStore.getTravelers()
  }

  // const fetchWinterSportPrice = async () => {
  //   if (withWinterSport) {
  //     winterSportPrice.value = await _fetchWinterSportPrice();
  //   }
  // };

  const onDelete = async (id: string) => {
    await emit('load', async () => {
      await travelersStore.deleteTraveler(id)
      await fetchTravellers()
    })
  }

  const onEdit = (id: string) => {
    userStore.setActiveInsuredTravelersSubPage('edit')
    if (id === HOLDER_ID) {
      isHolderFormActive.value = true
      return
    }
    const possibleTravelerToEdit = travelersInformation.value.find(
      (traveler) => String(traveler.srNo) === id
    )
    if (!possibleTravelerToEdit) return new Error('no traveler to edit')
    travelerInformation.value = possibleTravelerToEdit
    isTravelerFormActive.value = true
    fetchRelations()
  }

  const onAdd = () => {
    userStore.setActiveInsuredTravelersSubPage('add')
    travelerInformation.value = null
    isTravelerFormActive.value = true
    fetchRelations()
  }

  const saveTraveler = async (traveler: ApiTravelerInformation) => {
    await emit('load', async () => {
      await travelersStore.saveTraveler(traveler)
      await fetchTravellers()
      isTravelerFormActive.value = false
      isHolderFormActive.value = false

      if (userStore.activeInsuredTravelersSubPage === 'add') {
        gaService.sendInsuredTravelersAddNew(
          TravelDirection.OUTBOUND,
          traveler.relationName,
          traveler.nationalityName
        )
      } else {
        gaService.sendInsuredTravelersUpdateProceed(
          TravelDirection.OUTBOUND,
          traveler.nationalityName,
          traveler.relationName
        )
      }
    })
  }

  const fetchNationalities = async () => {
    if (commonStore.nationalities.length > 0) return

    const data = await commonStore.findNationalities()
    commonStore.setNationalities(data)
  }

  const fetchRelations = async () => {
    if (commonStore.relations.length > 0) return

    const data = await commonStore.getRelations()
    commonStore.setRelations(data)
  }

  onMounted(() => {
    emit('load', async () => {
      if (!(await getFormData(TravelDirection.OUTBOUND, true))) return
      await Promise.all([fetchTravellers(), fetchNationalities()])
      onPerformanceCheckEnd()
    })
  })

  // TODO: Refactor the way of handling go back
  watch(
    () => isTravelerFormActive.value,
    (newValue) => {
      commonStore.setStopBack(newValue)

      if (!newValue) {
        commonStore.setBackCallback(null)
        userStore.setActiveInsuredTravelersSubPage(undefined)
      } else {
        commonStore.setBackCallback(() => (isTravelerFormActive.value = false))
      }
    }
  )

  watch(
    () => isHolderFormActive.value,
    (newValue) => {
      commonStore.setStopBack(newValue)

      if (!newValue) {
        commonStore.setBackCallback(null)
        userStore.setActiveInsuredTravelersSubPage(undefined)
      } else {
        commonStore.setBackCallback(() => (isHolderFormActive.value = false))
      }
    }
  )

  onUnmounted(() => {
    commonStore.setStopBack(false)
    commonStore.setBackCallback(null)
    userStore.setActiveInsuredTravelersSubPage(undefined)
  })
</script>

<template>
  <FlowTravelerFormWidget
    v-if="isTravelerFormActive"
    :information="travelerInformation"
    :with-winter-sport="withWinterSport"
    :winter-sport-price="winterSportPrice"
    @save="saveTraveler"
  />
  <FlowPersonalInformationWidget
    v-else-if="isHolderFormActive"
    :with-winter-sport="false"
    :winter-sport-price="winterSportPrice"
    is-edit
    @load="emit('load', $event)"
    @proceed="isHolderFormActive = false"
  />
  <FlowInsuredTravelersWidget
    v-else
    :travelers="travelersInformation"
    @add="onAdd"
    @edit="onEdit"
    @delete="onDelete"
    @proceed="onProceed"
  />
</template>
